import React, { useMemo, useState } from 'react';
import { ChevronDownIcon } from '@bizfirst/common-react';
import type { NodeFormDto } from '@flow-studio/api';
import { buildFormMenuTree, type LabeledMenuItem } from '@flow-studio/atlas-forms-bridge';
import { mapNodeFormsToMenuItems } from './mapNodeFormsToMenuItems';

export interface FormLabelMenuProps {
  forms: NodeFormDto[];
  activeFormId: number | null;
  getDisplayTitle: (form: NodeFormDto) => string;
  onSelect: (formId: number) => void;
}

/**
 * Groups a node's forms into collapsible label folders (Atlas_Forms.Metadata.Labels),
 * falling back to a flat item at menu root level for any form with no usable
 * Metadata — see @flow-studio/atlas-forms-bridge's buildFormMenuTree.
 *
 * Drop-in alternative to FlatFormTabList; same props contract. See ./index.ts's
 * ENABLE_FORM_LABEL_MENU to swap between the two.
 */
export function FormLabelMenu({ forms, activeFormId, getDisplayTitle, onSelect }: FormLabelMenuProps) {
  const items = useMemo(() => mapNodeFormsToMenuItems(forms, getDisplayTitle), [forms, getDisplayTitle]);
  const tree = useMemo(() => buildFormMenuTree(items), [items]);

  // Per-folder collapse overrides, keyed by label name. Unset -> folder's own isCollapsedByDefault applies.
  const [collapsedOverrides, setCollapsedOverrides] = useState<Record<string, boolean>>({});

  const toggleFolder = (name: string, defaultCollapsed: boolean) => {
    setCollapsedOverrides((prev) => ({
      ...prev,
      [name]: !(prev[name] ?? defaultCollapsed),
    }));
  };

  const renderItem = (item: LabeledMenuItem<NodeFormDto>) => (
    <button
      key={item.id}
      className={`config-form-tab ${activeFormId === item.id ? 'active' : ''}`}
      onClick={() => onSelect(item.source.formID)}
      title={item.source.subtitle ? `${item.title} — ${item.source.subtitle}` : item.title}
    >
      <div className="config-form-tab-title">{item.title}</div>
    </button>
  );

  return (
    <>
      {tree.map((node) => {
        if (node.type === 'item') {
          return renderItem(node.item);
        }

        const collapsed = collapsedOverrides[node.name] ?? node.isCollapsedByDefault;
        return (
          <div key={node.name} className="config-form-folder">
            <button
              type="button"
              className="config-form-folder-header"
              onClick={() => toggleFolder(node.name, node.isCollapsedByDefault)}
              aria-expanded={!collapsed}
            >
              <ChevronDownIcon size={12} className={`config-form-folder-chevron ${collapsed ? 'is-collapsed' : ''}`} />
              <span className="config-form-folder-title">{node.title}</span>
            </button>
            {!collapsed && <div className="config-form-folder-items">{node.items.map(renderItem)}</div>}
          </div>
        );
      })}
    </>
  );
}
